import { memo, useCallback, useMemo, useState } from 'react'
import { z } from 'zod'
import { type Node, type NodeProps, Position } from '@xyflow/react'
import { isEmpty } from 'radash'
import { useShallow } from 'zustand/shallow'
import { useFlowStore } from '@/stores/flow-store'
import { useDeleteNode } from '@/hooks/use-delete-node'
import CustomHandle from '@/features/flow/_builder/components/handles/custom-handler'
import {
  NodeCard,
  NodeCardContent,
  NodeCardDescription,
  NodeCardFooter,
  NodeCardHeader,
} from '@/features/flow/_builder/components/ui/node-card'
import ActionNodePropertyPanel from '../../sidebar/panels/node-properties/property-panels/action-property-panel'
import { BuilderNode, RegisterNodeMetadata } from '../../types'
import { getNodeDetail } from '../../utils'

const NODE_TYPE = BuilderNode.ACTION

const ActionNodeActionSchema = z.object({
  type: z.enum([
    'add-tags',
    'remove-tags',
    'subscribe-sequence',
    'unsubscribe-sequence',
    'notify-team',
    'open-conversation',
    'close-conversation',
    'set-custom-fields',
    'clear-custom-fields',
  ]),
  payload: z.record(z.unknown()).optional(),
})

const ActionNodeDataSchema = z.object({
  actions: z
    .array(ActionNodeActionSchema)
    .min(1, 'Pelo menos uma ação deve ser configurada'),
  pauseOnOpenForHuman: z.boolean(),
  deletable: z.boolean().optional(),
})

export type ActionNodeAction = z.infer<typeof ActionNodeActionSchema>
export type ActionNodeData = z.infer<typeof ActionNodeDataSchema>

type ActionNodeProps = NodeProps<Node<ActionNodeData, typeof NODE_TYPE>>

export function ActionNode({
  id,
  isConnectable,
  selected,
  data,
}: ActionNodeProps) {
  const meta = useMemo(() => getNodeDetail(NODE_TYPE), [])

  const [showNodePropertiesOf] = useFlowStore(
    useShallow((s) => [s.actions.sidebar.showNodePropertiesOf])
  )

  const [sourceHandleId] = useState<string>(`${id}-source`)
  const [targetHandleId] = useState<string>(`${id}-target`)

  const deleteNode = useDeleteNode()

  const handleDeleteNode = () => {
    deleteNode(id)
  }

  const handleShowNodeProperties = useCallback(() => {
    showNodePropertiesOf({ id, type: NODE_TYPE })
  }, [id, showNodePropertiesOf])

  return (
    <NodeCard
      data-selected={selected}
      onDoubleClick={handleShowNodeProperties}
      nodeId={id}
    >
      <NodeCardHeader
        icon={meta.icon}
        title={meta.title}
        handleDeleteNode={handleDeleteNode}
        handleShowNodeProperties={handleShowNodeProperties}
        gradientColor={meta.gradientColor}
      />

      <NodeCardContent>
        <div className='flex flex-col p-4'>
          <div className='text-xs font-medium text-card-foreground'>Ações</div>

          <div className='mt-2 text-sm leading-snug'>
            {isEmpty(data.actions) ? (
              <span className='italic text-card-foreground'>
                Nenhuma ação definida...
              </span>
            ) : (
              <ul className='space-y-1 list-disc list-inside'>
                {data.actions.slice(0, 3).map((a, idx) => (
                  <li key={idx} className='truncate'>
                    {a.type}
                  </li>
                ))}
                {data.actions.length > 3 && (
                  <li className='italic text-card-foreground/70'>
                    +{data.actions.length - 3} outras
                  </li>
                )}
              </ul>
            )}
          </div>

          {data.pauseOnOpenForHuman && (
            <div className='inline-flex items-center gap-2 px-2 py-1 mt-3 text-xs text-yellow-700 rounded bg-yellow-500/10'>
              Pausar automação ao abrir para atendimento
            </div>
          )}
        </div>

        <NodeCardDescription description='Executa ações em background' />

        <NodeCardFooter nodeId={id} />
      </NodeCardContent>
      <CustomHandle
        type='target'
        id={targetHandleId}
        position={Position.Left}
        isConnectable={isConnectable}
      />

      <CustomHandle
        type='source'
        id={sourceHandleId}
        position={Position.Right}
        isConnectable={isConnectable}
      />
    </NodeCard>
  )
}

export const metadata: RegisterNodeMetadata<ActionNodeData> = {
  type: NODE_TYPE,
  node: memo(ActionNode),
  detail: {
    icon: 'bolt',
    title: 'Ação',
    description: 'Executa ações invisíveis ao usuário (background).',
    gradientColor: 'orange',
  },
  connection: {
    inputs: 1,
    outputs: 1,
  },
  defaultData: {
    actions: [],
    pauseOnOpenForHuman: true,
  },
  propertyPanel: ActionNodePropertyPanel,
  validationSchema: ActionNodeDataSchema,
}
